import { ApolloQueryResult, useLazyQuery, useMutation } from "@apollo/client";
import { clientNeoAuth } from "@neonumy/api-clients";
import { notification } from "@neonumy/neonumy-ui";
import * as Sentry from "@sentry/browser";
import {
  GET_USER_ACCESS_COMPANIES_QUERY,
  UPDATE_USER_COMPANIES_PERMISSIONS,
} from "queries/neoauth/users";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { onErrorGraphql } from "utils/onErrorGraphql";

interface UseDeleteUserAccessProps {
  userId: ID;
  accountingFirmId: ID;
  refetch: (
    variables?: Partial<ListAccountingFirmsAccessInput> | undefined,
  ) => Promise<ApolloQueryResult<ListAccountingFirmsAccessOutput>>;
}

export function useDeleteUserAccess({
  userId,
  accountingFirmId,
  refetch,
}: UseDeleteUserAccessProps) {
  const { t } = useTranslation();

  const [listAccountingFirmsAccess, { data: accessData, error: accessError }] = useLazyQuery<
    ListAccountingFirmsAccessOutput,
    ListAccountingFirmsAccessInput
  >(GET_USER_ACCESS_COMPANIES_QUERY, {
    client: clientNeoAuth,
  });

  useEffect(() => {
    if (accessData) {
      const { getUserAccesses } = accessData;
      if (
        getUserAccesses?.accountingFirms?.length === undefined ||
        getUserAccesses?.accountingFirms?.length === 0
      ) {
        Sentry.captureException(new Error("Invalid accounting firm access"), {
          extra: {
            response: getUserAccesses,
            accountingFirm: getUserAccesses?.accountingFirms,
          },
        });
      } else {
        updateCompanies({
          variables: {
            userId,
            removeAccountingFirms: [accountingFirmId],
            removeCompanies: getUserAccesses?.accountingFirms[0]?.companiesList,
          },
        });
      }
    }
  }, [accessData]);

  useEffect(() => {
    if (accessError) onErrorGraphql(accessError);
  }, [accessError]);

  const [updateCompanies] = useMutation<
    UpdateUserCompaniesMutationOutput,
    UpdateUserCompaniesMutationInput
  >(UPDATE_USER_COMPANIES_PERMISSIONS, {
    client: clientNeoAuth,
    onCompleted: ({ updateUserCompanies }) => {
      if (updateUserCompanies?.errors?.length === 0) {
        notification.success({
          message: `${t("User access")}`,
          description: t("Deleted Successfully"),
          placement: "bottomLeft",
        });
      }
      refetch();
    },
    onError: onErrorGraphql,
  });

  const handleOk = () => {
    if (userId && accountingFirmId) {
      listAccountingFirmsAccess({ variables: { id: userId, accountingFirmId } });
    }
  };

  return { handleOk };
}
